import React from 'react';
import { motion } from 'framer-motion';

export const DashboardSkeleton = () => (
  <motion.div 
    className="space-y-6 md:space-y-8 py-6 md:py-8"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    {/* Header Skeleton */}
    <div className="h-8 bg-muted rounded w-1/3 animate-pulse"></div>
    
    {/* Metric Cards Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
      {[1, 2, 3, 4]?.map((i) => (
        <motion.div 
          key={i} 
          className="bg-card rounded-xl border p-4 md:p-5 lg:p-6 shadow-elevation-1"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1, duration: 0.3 }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-muted animate-pulse"></div>
            <div className="w-16 h-6 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-2/3 animate-pulse"></div>
            <div className="h-8 bg-muted rounded w-1/2 animate-pulse"></div>
            <div className="h-3 bg-muted rounded w-1/3 animate-pulse"></div>
          </div>
        </motion.div>
      ))}
    </div>
    
    {/* Chart Skeleton */}
    <motion.div 
      className="bg-card rounded-xl border p-4 md:p-6 lg:p-8 shadow-elevation-1"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.3 }}
    >
      <div className="h-6 bg-muted rounded w-1/4 mb-4 animate-pulse"></div>
      <div className="h-64 md:h-80 lg:h-96 bg-muted rounded animate-pulse"></div>
    </motion.div>
  </motion.div>
);

export const LoadingSkeleton = () => (
  <motion.div 
    className="flex items-center justify-center min-h-[400px]"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="text-center">
      <motion.div 
        className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-muted-foreground" role="status" aria-live="polite">Loading...</p>
    </div>
  </motion.div>
);

export const TableSkeleton = () => (
  <div className="bg-card rounded-xl border shadow-elevation-1 overflow-hidden">
    <div className="divide-y">
      {[1, 2, 3, 4, 5]?.map((i) => (
        <motion.div 
          key={i} 
          className="p-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1, duration: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-3/4 animate-pulse"></div>
              <div className="h-3 bg-muted rounded w-1/2 animate-pulse"></div>
            </div>
            <div className="h-6 bg-muted rounded w-20 animate-pulse"></div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export const CardSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-muted animate-pulse"></div>
      <div className="w-16 h-6 bg-muted rounded animate-pulse"></div>
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-muted rounded w-2/3 animate-pulse"></div>
      <div className="h-6 bg-muted rounded w-1/2 animate-pulse"></div>
      <div className="h-2 bg-muted rounded w-full animate-pulse"></div>
    </div>
  </motion.div>
);

export const ChartSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="h-6 bg-muted rounded w-1/3 mb-4 animate-pulse"></div>
    <div className="h-64 md:h-80 bg-muted rounded animate-pulse"></div>
  </motion.div>
);

export const FormSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1 space-y-4"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="h-6 bg-muted rounded w-1/4 mb-4 animate-pulse"></div>
    <div className="space-y-3">
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
    </div>
    <div className="flex gap-3 pt-4">
      <div className="h-10 bg-muted rounded w-1/2 animate-pulse"></div>
      <div className="h-10 bg-muted rounded w-1/2 animate-pulse"></div>
    </div>
  </motion.div>
);

export const CalendarSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
      <div className="flex gap-2">
        <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
        <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
      </div>
    </div>
    <div className="grid grid-cols-7 gap-2">
      {[...Array(35)]?.map((_, i) => (
        <motion.div 
          key={i} 
          className="h-12 bg-muted rounded"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.02, duration: 0.2 }}
        />
      ))}
    </div>
  </motion.div>
);

export const PerformanceCardSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="h-5 bg-muted rounded w-1/3 animate-pulse"></div>
      <div className="h-8 w-16 bg-muted rounded-full animate-pulse"></div>
    </div>
    <div className="space-y-3">
      <div className="h-8 bg-muted rounded w-1/2 animate-pulse"></div>
      <div className="h-2 bg-muted rounded w-full animate-pulse"></div>
      <div className="flex gap-2">
        <div className="h-6 bg-muted rounded w-20 animate-pulse"></div>
        <div className="h-6 bg-muted rounded w-20 animate-pulse"></div>
      </div>
    </div>
  </motion.div>
);

export const PortfolioSkeleton = () => (
  <motion.div 
    className="space-y-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div className="h-6 bg-muted rounded w-1/3 animate-pulse"></div>
        <div className="h-8 bg-muted rounded w-24 animate-pulse"></div>
      </div>
      <div className="h-48 bg-muted rounded mb-4 animate-pulse"></div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full animate-pulse"></div>
          <div className="h-6 bg-muted rounded w-2/3 animate-pulse"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full animate-pulse"></div>
          <div className="h-6 bg-muted rounded w-2/3 animate-pulse"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full animate-pulse"></div>
          <div className="h-6 bg-muted rounded w-2/3 animate-pulse"></div>
        </div>
      </div>
    </div>
  </motion.div>
);

export const ScenarioCardSkeleton = () => (
  <motion.div 
    className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 rounded-full bg-muted animate-pulse"></div>
      <div className="flex-1 space-y-2">
        <div className="h-5 bg-muted rounded w-1/2 animate-pulse"></div>
        <div className="h-4 bg-muted rounded w-3/4 animate-pulse"></div>
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
      <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
    </div>
  </motion.div>
);
